import mime from 'mime';
import truncate from 'lodash/truncate';
import {
  extractSnippet,
  formatHtmlInput, getHtmlBodyContent,
  highlightAndLinkString,
  highlightSearchTerm, markLinks,
} from './various';
import {endsWith, orderBy, omit, isArray, isObject, forOwn} from 'lodash';
import sanitizeHtml from 'sanitize-html';
import ReactHtmlParser from 'react-html-parser';
import SubscriptionsStore from '../xstores/subscriptions';
import {addInlineFiles} from './gmail';
import {formatTeamsResult, getTeamsAttachments} from './teams';

const NO_PREVIEW_AVAILABLE = 'No preview available. Click here to check the content.';
const NO_CONTENT_AVAILABLE = 'No content available.'

const getUserEmailFromSub = (data:any) => {
  const subscription = SubscriptionsStore.normalized$.getValue();
  // @ts-ignore
  const officeSubscriptions = subscription['office-365'];
  const emailAddresses:string[] = [];
  if (data.from?.emailAddress?.address) {
    emailAddresses.push(data.from.emailAddress.address)
  }
  if (data.toRecipients && data.toRecipients.length) {
    data.toRecipients.forEach((rec:any) => {
      if (rec.emailAddress && rec.emailAddress.address) {
        emailAddresses.push(rec.emailAddress.address);
      }
    })
  }
  let email = '';
  if (emailAddresses.length && officeSubscriptions) {
    officeSubscriptions.forEach((sub:any) => {
      if (emailAddresses.indexOf(sub.email) > -1) {
        email = sub.email;
      }
    });
  }
  if (!email) {
    email = officeSubscriptions ? officeSubscriptions[0].email : '';
  }

  return email;
}

const getOfficeConnectedAccounts = () => {
  const subscription = SubscriptionsStore.normalized$.getValue();
  // @ts-ignore
  const officeSubscriptions = subscription['office-365'];
  return officeSubscriptions.map((s:any) => s.email);
}

interface mappersProps{
  [hey:string]: Function,
}
const mappers:mappersProps = {
  onedrive: (data:any, highlights:string[]) => {
    return {
      title: highlightSearchTerm(data.name, highlights),
      filename: data.name,
      mimeType: mime.getType(data.name),
      date: new Date(data.modified_date || data.created_date).toLocaleString(),
      permalink: data.url
    }
  },
  files: (data: any, highlights:string[]) => {
    let ownership = 'Owned by you';
    const createdBy = data.createdBy.user;
    const lastModifiedBy = data.lastModifiedBy.user;
    const oAccounts = getOfficeConnectedAccounts();
    if (oAccounts.indexOf(createdBy.email) === -1) {
      ownership = `Shared by ${createdBy.displayName}`
    }
    if (oAccounts.indexOf(lastModifiedBy?.email) > -1) {
      data.lastModifiedBy.user.displayName = 'you';
    }
    return {
      title: highlightSearchTerm(data.name, highlights),
      filename: data.name,
      mimeType: data.folder ? 'folder' : data.file?.mimeType || mime.getType(data.name),
      date: new Date(data.lastModifiedDateTime || data.createdDateTime).toLocaleString(),
      permalink: data.webUrl,
      ownership,
    }
  },
  sharepoint: (data:any, highlights:string[]) => {
    const resultMessage = highlightAndLinkString(data.description, highlights);
    const resultSnippet = extractSnippet(resultMessage);
    return {
      title: highlightSearchTerm(data.display_name, highlights),
      resultSnippet: ReactHtmlParser(resultSnippet),
      resultMessage: ReactHtmlParser(resultMessage),
      filename: data.name,
      mimeType: mime.getType(data.name),
      date: new Date(data.modified_date || data.created_date).toLocaleString(),
      permalink: data.url
    }
  },
  outlook: (data: any, highlights: string[]) => {
    let sender = '';
    if (data.from && data.from.emailAddress) {
      const { emailAddress } = data.from;
      sender = `<${emailAddress.address}>`;
      if (emailAddress.name && emailAddress.name !== emailAddress.address) {
        sender = `${emailAddress.name} ${sender}`;
      }
    }
    const { content } = data.body;
    const emailContent = getHtmlBodyContent(content);

    let trimmedSnippet = truncate(emailContent.text.replace(/[\n\r\s\t]+/g, ' ').trim(), { length: 220 });
    // add "..." at the end of every outlook snippet

    if (trimmedSnippet && !endsWith(trimmedSnippet,'...')) {
      trimmedSnippet += '...';
    }
    const resultSnippet = trimmedSnippet ? markLinks(trimmedSnippet) : NO_PREVIEW_AVAILABLE;
    let files = [];
    if (data.attachments && isArray(data.attachments)) {
      files = data.attachments
        .map((att:any) => ({
          imgId: att.contentId,
          isInline: att.isInline,
          content: att.contentBytes,
          mimeType: mime.getType(att.name),
        }))
    }
    if (isObject(data.attachments)) {
      forOwn(data.attachments, (value, key) => {
        files.push({
          name: key,
          url: value.url,
          mimeType: value.type,
        })
      })
    }
    const email = getUserEmailFromSub(data);
    const isSent = sender.indexOf(email) > -1;
    const permalink = data.url || `https://outlook.office.com/mail/${isSent ? 'sentitems': 'inbox'}/id/${encodeURIComponent(data.conversationId).replace('_', '%2B')}`;
    const att = data.hasAttachments ?
      data.attachments
        .filter((at:any) => !at.isInline)
        .map((at:any) => {
          return {
            filename: highlightSearchTerm(at.name || '', highlights),
            mimeType: mime.getType(at.name),
            url: at.attachment || permalink,
          }
        }) : [];

    return {
      title: highlightSearchTerm(data.subject, highlights) || 'No Subject',
      sender: sender || 'No sender information',
      emailContent,
      files,
      resultSnippet: ReactHtmlParser(sanitizeHtml(resultSnippet)),
      resultMessage() {
        // on demand parsing of full Email Body
        if (this.fullMessage) {
          return this.fullMessage;
        }
        let fullBody:any = `<div style="${emailContent.style ? `${emailContent.style};` : ''} max-width: 100%; word-break: break-all">${this.emailContent.body.innerHTML}</div>`
        if (!this.emailContent.text.replace(/[\n\r\s\t]+/g, ' ').trim()) {
          return this.fullMessage = NO_CONTENT_AVAILABLE;
        }
        fullBody = addInlineFiles(fullBody, this.files);
        fullBody = ReactHtmlParser(fullBody);
        return this.fullMessage = fullBody;
      },
      fullMessage: null as any,
      permalink,
      email,
      att,
      date: new Date(data.receivedDateTime).toLocaleString(),
    }
  },
  calendar: (data: any, highlights: string[]) => {
    let resultMessage = formatHtmlInput(data.body?.content, highlights);
    const msg = data.body?.contentType === 'html' ? getHtmlBodyContent(resultMessage).text : data.body?.content;
    const resultSnippet = truncate(msg || '', { length: 220 });
    resultMessage = ReactHtmlParser(sanitizeHtml(resultMessage));
    const dateOptions:Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }
    let dateString = 'Not set';
    let attachments = [];
    if (data.start) {
      dateString = new Date(data.start.dateTime).toLocaleString('en-US', dateOptions);
      if (data.end) {
        dateString = `${dateString} - ${new Date(data.end.dateTime).toLocaleString('en-US', {hour: '2-digit', minute: '2-digit'})}`
      }
    }
    const attendees = data.attendees.map((att: any) => {
      let name = att.displayName;
      if (!name && att.emailAddress) {
        name = att.emailAddress.name || att.emailAddress.address;
      }
      return name
    }).filter((a:string) => a);
    if (data.attachments && data.attachments.length) {
      attachments = data.attachments.map((att: any) => {
        return {
          filename: att.name,
          mimeType: mime.getType(att.name)
        }
      });
    }
    return {
      title: highlightSearchTerm(data.subject, highlights),
      resultSnippet: ReactHtmlParser(sanitizeHtml(resultSnippet)),
      resultMessage: resultMessage,
      dateString,
      permalink: truncate(data.meeting_url, { length: 30 }),
      meetingAttendees: attendees,
      displayOrganizer: data.organizer || 'Unknown',
      att: attachments,
    }
  },
  contacts: (data: any, highlights: string[]) => {
    return {
      title: highlightSearchTerm(data.displayName, highlights),
      contactFrom: getUserEmailFromSub(data),
    }
  },
  teams: (data: any, highlight: string[]) => {
    let teamDisplayName = data.team_name, channelDisplayName = data.channel_name;
    let permalink = '';
    if (data.chat_type === 'chat') {
      permalink = data.chat_webUrl;
    } else if (data.chat_type === 'team_channel') {
      permalink = data.webUrl;
    } else if (data.match_type === 'reply'){
      if (+data.match_reply_pos > -1) {
        permalink = data.replies[+data.match_reply_pos]?.webUrl;
      } else {
        permalink = data.webUrl;
      }
    }

    const processedResult = formatTeamsResult(data.body.content, highlight);
    const attachments = getTeamsAttachments(data);

    // display data with original message
    const displayData = {
      resultSnippet: processedResult.parsedSnippet,
      resultText: processedResult.parsedText,
      sender: data.from?.user?.displayName || '',
      isMatch: data.match_reply_pos < 0,
    };

    let conversation = data.replies?.map((r:any, i:number) => {
      const { content } = r.body;
      const processedReply = formatTeamsResult(content, highlight);
      const att = getTeamsAttachments(r);
      return {
        ...r,
        att,
        resultText: processedReply.parsedText,
        resultSnippet: processedReply.parsedSnippet,
        sender: r.from?.user?.displayName || '',
        isMatch: i === +data.match_reply_pos,
      }
    }) || [];
    // Ensure replies ordering
    // Order them by createdDateTime
    conversation = orderBy(conversation, (r) => {
      return new Date(r.createdDateTime);
    }, 'asc');
    // add original message in the conversation as the first message
    conversation.unshift(omit({
      ...data,
      ...displayData,
      attachments,
    }, 'replies'));

    if (data.match_type === 'reply' && +data.match_reply_pos !== -1) {
      // get reply by old position
      const matchedReply = data.replies ? data.replies[+data.match_reply_pos] : null;
      // find new position
      if (matchedReply) {
        const foundIdx = conversation.findIndex((cr:any) => {
          if (data.match_type === 'reply') {
            return cr.reply_msg_id === matchedReply.reply_msg_id;
          }
          return cr.msg_id === matchedReply.msg_id
        });
        if (foundIdx > -1) {
          data.match_reply_pos = foundIdx;
          displayData.resultSnippet = conversation[foundIdx].resultSnippet;
          displayData.resultText = conversation[foundIdx].resultText;
          displayData.sender = conversation[foundIdx].from?.user?.displayName || 'No sender information';
        }
      }
    }

    return {
      permalink,
      att: attachments,
      ...displayData,
      conversation,
      teamDisplayName: teamDisplayName || '',
      channelDisplayName,
      date: data.createdDateTime ? new Date(data.createdDateTime).toLocaleString() : '',
    }
  },
}

const structureOfficeResult = (item:any, highlight:string[]) => {
  if (!item || !item['o365-platform'] || !mappers[item['o365-platform']]) {
    return {};
  }
  return {
    ...item,
    pl: 'office-365',
    ...mappers[item['o365-platform']](item, highlight),
  }
}

export default structureOfficeResult;